import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import Shepherd from 'shepherd.js';
import { DEFAULT_TOUR_OPTIONS } from './shepherd-defaults';
import {
  isTourCompleted as isTourCompletedInStorage,
  markTourCompleted,
  resetTour as resetTourInStorage,
  resetAllTours as resetAllToursInStorage,
} from './shepherd-storage';
import {
  canAutoStartShepherdTour,
  clearAllShepherdTourPersistenceForTesting,
  markShepherdTourAutoStartedThisLogin,
  markShepherdTourPermanentlySkipped,
} from './shepherd-tour-login-state';
import { environment } from '../../../../environments/environment';
import {
  EXPLORE_ACTIVITY_TOUR,
  EXPLORE_ACTIVITY_CONTINUATION_TOUR,
  EXPLORE_JOIN_REQUEST_STEP_ID,
  EXPLORE_SEARCH_WAIT_STEP_ID,
  buildExploreActivityTour,
  buildExploreActivityContinuationTour,
} from './tours/explore-activity.tour';
import { HOME_PAGE_TOUR, buildHomePageTour } from './tours/home-page.tour';
import {
  isEditableFocusInsideElement,
  isShepherdLocationTypingLocked,
  isShepherdTourStepInteractive,
  registerTourDismissProtection,
  setShepherdTourUiChangedNotifier,
  SHEPHERD_TOUR_CLICK_SHIELD_CLASS,
  shouldSkipShepherdStepReshow,
  wasShepherdStepDismissedByUser,
  type ShepherdStep,
} from './shepherd-interactive-step';
import {
  releaseAllShepherdTourFieldLocks,
  setShepherdTourFieldLockNotifier,
} from './shepherd-tour-field-lock';
import {
  clearExploreSearchTourUi,
  resetExploreSearchTargetPadding,
  resolveExploreSearchTarget,
  setExploreSearchInteractionActive,
} from './explore-search.shepherd';
import {
  clearPostActivityLocationTourUi,
  POST_ACTIVITY_LOCATION_STEP_ID,
  resetPostActivityLocationTargetPadding,
  resolvePostActivityLocationTarget,
  setPostActivityLocationInteractionActive,
  setPostActivityTourBodyActive,
} from './post-activity-location.shepherd';
import {
  clearTrainingTourUi,
  resetTrainingFiltersTargetPadding,
  resolveTrainingFiltersTarget,
  setTrainingFiltersDatepickerOpen,
  setTrainingFiltersInteractionActive,
  TRAINING_FILTERS_STEP_ID,
} from './training-filters.shepherd';
import {
  advancePostActivityTourFromActivityTypeStep,
  POST_ACTIVITY_TOUR,
  buildPostActivityTour,
} from './tours/post-activity.tour';
import {
  resetMessagesConnectionsSearchTargetPadding,
  resolveMessagesConnectionsSearchTarget,
  setMessagesConnectionsSearchInteractionActive,
} from './messages-connections-search.shepherd';
import {
  MESSAGES_CONNECTIONS_SEARCH_STEP_IDS,
  MESSAGES_CONNECTIONS_TOUR,
  buildMessagesConnectionsTour,
  isMessagesConnectionsSkeletonVisible,
  messagesConnectionsHasEmptyState,
  messagesConnectionsHasList,
} from './tours/messages-connections.tour';
import {
  MESSAGES_GROUP_CHAT_TOUR,
  buildMessagesGroupChatTour,
  isGroupChatsTabActiveInDom,
  isMessagesGroupChatsSkeletonVisible,
} from './tours/messages-group-chat.tour';
import {
  TRAINING_BOOK_TRAINER_TOUR,
  TRAINING_RESULTS_STEP_ID,
  buildTrainingBookTrainerTour,
  tryShowTrainingResultsStepIfReady,
} from './tours/training-book-trainer.tour';
import {
  TRAINING_BOOK_SESSION_TOUR,
  TRAINING_BOOK_SESSION_TOUR_PENDING_KEY,
  buildTrainingBookSessionTour,
  clearTrainingBookSessionTourUi,
} from './tours/training-book-session.tour';
import { SCHEDULE_PAGE_TOUR, buildSchedulePageTour } from './tours/schedule-page.tour';
import {
  TEE_TIME_POLL_STEP_COURSES,
  TEE_TIME_POLL_STEP_DATES,
  TEE_TIME_POLL_STEP_TIMES,
  TEE_TIME_POLL_TOUR,
  buildTeeTimePollTour,
  getTeeTimePollTourStepIdForModalStep,
  setTeeTimePollTourBodyActive,
  prepareTeeTimePollTourForModalStep,
  showTeeTimePollTourStepWhenReady,
} from './tours/tee-time-poll.tour';
import {
  resetTeeTimePollCoursesTargetPadding,
  resolveTeeTimePollCoursesTarget,
  setTeeTimePollCoursesInteractionActive,
} from './tee-time-poll-courses.shepherd';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

type TourFactory = (tour: ShepherdTour) => void;

@Injectable({
  providedIn: 'root',
})
export class ShepherdService implements OnDestroy {
  private activeTour: ShepherdTour | null = null;
  private readonly tourRegistry = new Map<string, TourFactory>();
  private readonly tourUiChanged = new Subject<void>();
  /** Emits when a step tip opens/closes so hosts can refresh `backDisabled` bindings. */
  readonly tourUiChanged$: Observable<void> = this.tourUiChanged.asObservable();
  private routerSub: Subscription;
  /** When true, `cancel` was triggered by the app (navigation, replace tour, etc.), not the user skipping. */
  private suppressPermanentSkipOnTourCancel = false;
  /** Location-step tip hidden while user types (mobile keyboard). */

  constructor(
    private readonly router: Router,
    private readonly ngZone: NgZone,
  ) {
    setShepherdTourFieldLockNotifier(() => this.notifyTourUiChanged());
    setShepherdTourUiChangedNotifier(() => this.notifyTourUiChanged());
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe((e) => {
        const nav = e as NavigationStart;
        if (!this.activeTour) {
          return;
        }
        if (!this.isShepherdActiveTour(this.activeTour)) {
          return;
        }
        const nextPath = (nav.url || '').split('?')[0].split('#')[0];
        const tourId = this.activeTour.options?.id as string | undefined;
        if (tourId === EXPLORE_ACTIVITY_TOUR && nextPath.includes('explore-activity')) {
          return;
        }
        if (tourId === EXPLORE_ACTIVITY_CONTINUATION_TOUR && nextPath.includes('explore-activity')) {
          return;
        }
        if (tourId === HOME_PAGE_TOUR && this.isHomePagePath(nextPath)) {
          return;
        }
        if (
          tourId === POST_ACTIVITY_TOUR &&
          (nextPath.includes('add-activity') || nextPath.includes('explore-activity'))
        ) {
          return;
        }
        if (tourId === MESSAGES_CONNECTIONS_TOUR && nextPath.includes('messages')) {
          return;
        }
        if (tourId === MESSAGES_GROUP_CHAT_TOUR && nextPath.includes('messages')) {
          return;
        }
        if (tourId === TRAINING_BOOK_TRAINER_TOUR && (nextPath === '/training' || nextPath === '/training/')) {
          return;
        }
        if (tourId === TRAINING_BOOK_SESSION_TOUR && /^\/training\/book(\/|$)/.test(nextPath)) {
          return;
        }
        if (tourId === SCHEDULE_PAGE_TOUR && this.isSchedulePagePath(nextPath)) {
          return;
        }
        this.cancelActiveTourWithoutRecordingSkip();
      });
  }

  private cancelActiveTourWithoutRecordingSkip(): void {
    const t = this.activeTour;
    if (!t || !this.isShepherdActiveTour(t)) {
      this.activeTour = null;
      return;
    }
    this.suppressPermanentSkipOnTourCancel = true;
    void t.cancel();
    this.activeTour = null;
  }

  /** Shepherd’s `tour.isActive()` compares global `Shepherd.activeTour` — use this for cancel/skip decisions. */
  private isShepherdActiveTour(tour: ShepherdTour): boolean {
    return typeof Shepherd !== 'undefined' && Shepherd.activeTour === tour;
  }

  private getCurrentStepSafe(tour: ShepherdTour): { id: string } | null | undefined {
    return tour.getCurrentStep?.() ?? tour.currentStep ?? null;
  }

  registerTour(name: string, factory: TourFactory): void {
    this.tourRegistry.set(name, factory);
  }

  startTour(name: string, force = false): void {
    if (!force && isTourCompletedInStorage(name)) {
      return;
    }
    if (force && !canAutoStartShepherdTour(name)) {
      return;
    }
    if (this.activeTour) {
      const activeId = this.activeTour.options?.id as string | undefined;
      if (force && activeId === name && this.isShepherdActiveTour(this.activeTour)) {
        return;
      }
      if (force) {
        this.cancelActiveTourWithoutRecordingSkip();
      } else if (this.isShepherdActiveTour(this.activeTour)) {
        return;
      } else {
        this.activeTour = null;
      }
    }
    const factory = this.tourRegistry.get(name);
    if (!factory) {
      return;
    }
    const tour = new Shepherd.Tour({
      ...DEFAULT_TOUR_OPTIONS,
      ...(name === POST_ACTIVITY_TOUR
        ? {
            defaultStepOptions: {
              ...DEFAULT_TOUR_OPTIONS.defaultStepOptions,
              scrollTo: false,
            },
          }
        : {}),
      ...(name === EXPLORE_ACTIVITY_TOUR || name === MESSAGES_CONNECTIONS_TOUR
        ? {
            useModalOverlay: false,
            defaultStepOptions: {
              ...DEFAULT_TOUR_OPTIONS.defaultStepOptions,
              scrollTo: false,
            },
          }
        : {}),
      ...(name === TRAINING_BOOK_TRAINER_TOUR
        ? {
            defaultStepOptions: {
              ...DEFAULT_TOUR_OPTIONS.defaultStepOptions,
              scrollTo: false,
            },
          }
        : {}),
      ...(name === TEE_TIME_POLL_TOUR
        ? {
            defaultStepOptions: {
              ...DEFAULT_TOUR_OPTIONS.defaultStepOptions,
              scrollTo: false,
            },
          }
        : {}),
      id: name,
    });
    factory(tour);
    registerTourDismissProtection(tour);

    if (name === POST_ACTIVITY_TOUR) {
      tour.on('start', () => {
        setPostActivityTourBodyActive(true);
      });
    }

    tour.on('complete', () => {
      if (name === POST_ACTIVITY_TOUR) {
        setPostActivityTourBodyActive(false);
        clearPostActivityLocationTourUi();
        releaseAllShepherdTourFieldLocks();
      }
      if (name === EXPLORE_ACTIVITY_TOUR) {
        clearExploreSearchTourUi();
      }
      if (name === TRAINING_BOOK_TRAINER_TOUR) {
        clearTrainingTourUi();
      }
      if (name === MESSAGES_CONNECTIONS_TOUR) {
        setMessagesConnectionsSearchInteractionActive(false);
        resetMessagesConnectionsSearchTargetPadding();
      }
      if (name === TEE_TIME_POLL_TOUR) {
        setTeeTimePollTourBodyActive(false);
        setTeeTimePollCoursesInteractionActive(false);
        resetTeeTimePollCoursesTargetPadding();
      }
      if (
        name === EXPLORE_ACTIVITY_TOUR ||
        name === TRAINING_BOOK_TRAINER_TOUR ||
        name === MESSAGES_CONNECTIONS_TOUR ||
        name === TEE_TIME_POLL_TOUR
      ) {
        releaseAllShepherdTourFieldLocks();
      }
      markTourCompleted(name);
      if (name === EXPLORE_ACTIVITY_CONTINUATION_TOUR) {
        markTourCompleted(EXPLORE_ACTIVITY_TOUR);
      }
      this.activeTour = null;
      this.notifyTourUiChanged();
    });
    tour.on('cancel', () => {
      if (name === POST_ACTIVITY_TOUR) {
        setPostActivityTourBodyActive(false);
        clearPostActivityLocationTourUi();
        releaseAllShepherdTourFieldLocks();
      }
      if (name === EXPLORE_ACTIVITY_TOUR) {
        clearExploreSearchTourUi();
      }
      if (name === TRAINING_BOOK_TRAINER_TOUR) {
        clearTrainingTourUi();
      }
      if (name === MESSAGES_CONNECTIONS_TOUR) {
        setMessagesConnectionsSearchInteractionActive(false);
        resetMessagesConnectionsSearchTargetPadding();
      }
      if (name === TEE_TIME_POLL_TOUR) {
        setTeeTimePollTourBodyActive(false);
        setTeeTimePollCoursesInteractionActive(false);
        resetTeeTimePollCoursesTargetPadding();
      }
      if (
        name === EXPLORE_ACTIVITY_TOUR ||
        name === TRAINING_BOOK_TRAINER_TOUR ||
        name === MESSAGES_CONNECTIONS_TOUR ||
        name === TEE_TIME_POLL_TOUR
      ) {
        releaseAllShepherdTourFieldLocks();
      }
      if (!this.suppressPermanentSkipOnTourCancel) {
        markShepherdTourPermanentlySkipped(name);
      } else {
        this.suppressPermanentSkipOnTourCancel = false;
      }
      if (name === TRAINING_BOOK_SESSION_TOUR) {
        try {
          sessionStorage.removeItem(TRAINING_BOOK_SESSION_TOUR_PENDING_KEY);
        } catch {
          // ignore
        }
      }
      this.activeTour = null;
      this.notifyTourUiChanged();
    });
    if (name === TEE_TIME_POLL_TOUR) {
      tour.on('start', () => {
        setTeeTimePollTourBodyActive(true);
      });
    }
    if (name === TRAINING_BOOK_SESSION_TOUR) {
      tour.on('start', () => {
        try {
          sessionStorage.removeItem(TRAINING_BOOK_SESSION_TOUR_PENDING_KEY);
        } catch {
          // ignore
        }
      });
    }
    if (force) {
      tour.on('start', () => {
        markShepherdTourAutoStartedThisLogin(name);
      });
    }

    this.activeTour = tour;
    this.ngZone.runOutsideAngular(() => {
      void tour.start();
    });
  }

  stopTour(): void {
    if (this.activeTour && this.isShepherdActiveTour(this.activeTour)) {
      this.suppressPermanentSkipOnTourCancel = true;
      void this.activeTour.cancel();
    }
    this.activeTour = null;
  }

  /** Cancel only if the active Shepherd tour matches `tourId` (e.g. close create-activity modal). */
  stopTourIfId(tourId: string): void {
    const t = this.activeTour;
    if (!t || (t.options?.id as string | undefined) !== tourId) {
      return;
    }
    if (tourId === TEE_TIME_POLL_TOUR) {
      setTeeTimePollTourBodyActive(false);
      setTeeTimePollCoursesInteractionActive(false);
      resetTeeTimePollCoursesTargetPadding();
      releaseAllShepherdTourFieldLocks();
    }
    if (tourId === POST_ACTIVITY_TOUR) {
      setPostActivityTourBodyActive(false);
      clearPostActivityLocationTourUi();
      releaseAllShepherdTourFieldLocks();
    }
    if (tourId === EXPLORE_ACTIVITY_TOUR) {
      clearExploreSearchTourUi();
      releaseAllShepherdTourFieldLocks();
    }
    if (tourId === TRAINING_BOOK_TRAINER_TOUR) {
      clearTrainingTourUi();
      releaseAllShepherdTourFieldLocks();
    }
    if (tourId === TRAINING_BOOK_SESSION_TOUR) {
      clearTrainingBookSessionTourUi();
    }
    if (tourId === MESSAGES_CONNECTIONS_TOUR) {
      setMessagesConnectionsSearchInteractionActive(false);
      resetMessagesConnectionsSearchTargetPadding();
      releaseAllShepherdTourFieldLocks();
    }
    if (this.isShepherdActiveTour(t)) {
      this.suppressPermanentSkipOnTourCancel = true;
      void t.cancel();
    }
    this.activeTour = null;
    if (
      tourId === TRAINING_BOOK_TRAINER_TOUR ||
      tourId === TRAINING_BOOK_SESSION_TOUR ||
      tourId === TEE_TIME_POLL_TOUR
    ) {
      this.removeDetachedShepherdTooltips();
    }
  }

  /** Removes leftover Shepherd DOM after training tour cancel (prevents orphan tips on navigation). */
  private removeDetachedShepherdTooltips(): void {
    document.querySelectorAll('.shepherd-element').forEach((node) => {
      node.remove();
    });
  }

  hasCompletedTour(name: string): boolean {
    return isTourCompletedInStorage(name);
  }

  resetTour(name: string): void {
    resetTourInStorage(name);
  }

  resetAllTours(): void {
    resetAllToursInStorage();
    clearAllShepherdTourPersistenceForTesting();
    try {
      sessionStorage.removeItem(TRAINING_BOOK_SESSION_TOUR_PENDING_KEY);
    } catch {
      // ignore
    }
  }

  get isActive(): boolean {
    return !!(this.activeTour && this.isShepherdActiveTour(this.activeTour));
  }

  private isExploreActivityPath(url: string): boolean {
    const path = url.split('?')[0];
    return path.includes('explore-activity');
  }

  private isHomePagePath(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    const p = path === '' ? '/' : path;
    // Logged-in dashboard: root (`/`) embeds HomePage; `/home` is the same module route.
    return p === '/' || p === '/home' || p.startsWith('/home/');
  }

  private isSchedulePagePath(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/schedule' || path === '/schedule/';
  }

  registerExploreActivityTour(): void {
    this.registerTour(EXPLORE_ACTIVITY_TOUR, (tour) => buildExploreActivityTour(tour));
    this.registerTour(EXPLORE_ACTIVITY_CONTINUATION_TOUR, (tour) => buildExploreActivityContinuationTour(tour));
  }

  registerHomePageTour(): void {
    this.registerTour(HOME_PAGE_TOUR, (tour) => buildHomePageTour(tour));
  }

  registerPostActivityTour(): void {
    this.registerTour(POST_ACTIVITY_TOUR, (tour) => buildPostActivityTour(tour));
  }

  isPostActivityTourActive(): boolean {
    const t = this.activeTour;
    if (!t || !this.isShepherdActiveTour(t)) {
      return false;
    }
    return (t.options?.id as string | undefined) === POST_ACTIVITY_TOUR;
  }

  isPostActivityLocationStepActive(): boolean {
    if (!this.isPostActivityTourActive()) {
      return false;
    }
    const t = this.activeTour;
    if (!t) {
      return false;
    }
    return isShepherdTourStepInteractive(t, POST_ACTIVITY_LOCATION_STEP_ID);
  }

  /** Modal step-1 Next: advance tour to location when the form moves to Activity Details. */
  advancePostActivityTourAfterActivityTypeNext(): void {
    const t = this.activeTour;
    if (!t || !this.isPostActivityTourActive()) {
      return;
    }
    advancePostActivityTourFromActivityTypeStep(t);
  }

  /**
   * Call from location field focus / input / dropdown changes (modal + /add-activity page).
   */
  syncPostActivityLocationFieldWithTour(
    showDropdown: boolean,
    action: 'focus' | 'blur' | 'pointerdown' | 'dropdown',
  ): void {
    if (this.blockTourFieldSyncWhileTipOpen(() => this.isPostActivityLocationStepActive())) {
      return;
    }
    this.syncAutocompleteTourField(
      () => this.isPostActivityLocationStepActive(),
      setPostActivityLocationInteractionActive,
      resetPostActivityLocationTargetPadding,
      resolvePostActivityLocationTarget,
      showDropdown,
      action,
      true,
    );
  }

  isExploreSearchStepActive(): boolean {
    const t = this.activeTour;
    if (!t || !this.isShepherdActiveTour(t)) {
      return false;
    }
    if ((t.options?.id as string | undefined) !== EXPLORE_ACTIVITY_TOUR) {
      return false;
    }
    return isShepherdTourStepInteractive(t, EXPLORE_SEARCH_WAIT_STEP_ID);
  }

  isTrainingFiltersStepActive(): boolean {
    if (!this.isTrainingBookListTourActive()) {
      return false;
    }
    const t = this.activeTour;
    if (!t) {
      return false;
    }
    return isShepherdTourStepInteractive(t, TRAINING_FILTERS_STEP_ID);
  }

  isMessagesConnectionsSearchStepActive(): boolean {
    const t = this.activeTour;
    if (!t || !this.isShepherdActiveTour(t)) {
      return false;
    }
    if ((t.options?.id as string | undefined) !== MESSAGES_CONNECTIONS_TOUR) {
      return false;
    }
    const stepId = this.getCurrentStepSafe(t)?.id;
    if (
      !stepId ||
      !(MESSAGES_CONNECTIONS_SEARCH_STEP_IDS as readonly string[]).includes(stepId)
    ) {
      return false;
    }
    return isShepherdTourStepInteractive(t, stepId);
  }

  private refreshAutocompleteTourOverlayAfterDropdownClosed(
    isStepActive: () => boolean,
    resetPadding: () => void,
    resolveTourRoot: () => HTMLElement | null,
    allowStepUpdate: boolean,
  ): void {
    if (!isStepActive()) {
      return;
    }
    if (
      isShepherdLocationTypingLocked() ||
      !allowStepUpdate ||
      isEditableFocusInsideElement(resolveTourRoot())
    ) {
      return;
    }
    resetPadding();
    const step = this.activeTour?.getCurrentStep?.() as { update?: () => void } | undefined;
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        resetPadding();
        if (!isStepActive()) {
          return;
        }
        if (
          isShepherdLocationTypingLocked() ||
          !allowStepUpdate ||
          isEditableFocusInsideElement(resolveTourRoot())
        ) {
          return;
        }
        step?.update?.();
      });
    });
  }

  private syncAutocompleteTourField(
    isStepActive: () => boolean,
    setInteraction: (active: boolean) => void,
    resetPadding: () => void,
    resolveTourRoot: () => HTMLElement | null,
    showDropdown: boolean,
    action: 'focus' | 'blur' | 'pointerdown' | 'dropdown',
    allowStepUpdate: boolean,
  ): void {
    if (!isStepActive()) {
      return;
    }
    setInteraction(true);
    if (
      isShepherdLocationTypingLocked() ||
      isEditableFocusInsideElement(resolveTourRoot()) ||
      action === 'focus' ||
      action === 'pointerdown'
    ) {
      // No layout mutation or step.update while typing — refresh only after real blur.
      return;
    }
    resetPadding();
    if (!showDropdown && (action === 'dropdown' || action === 'blur')) {
      if (action === 'blur' && isEditableFocusInsideElement(resolveTourRoot())) {
        return;
      }
      this.refreshAutocompleteTourOverlayAfterDropdownClosed(
        isStepActive,
        resetPadding,
        resolveTourRoot,
        allowStepUpdate,
      );
    }
  }

  syncExploreSearchFieldWithTour(
    showDropdown: boolean,
    action: 'focus' | 'blur' | 'pointerdown' | 'dropdown',
  ): void {
    if (this.blockTourFieldSyncWhileTipOpen(() => this.isExploreSearchStepActive())) {
      return;
    }
    this.syncAutocompleteTourField(
      () => this.isExploreSearchStepActive(),
      setExploreSearchInteractionActive,
      resetExploreSearchTargetPadding,
      resolveExploreSearchTarget,
      showDropdown,
      action,
      false,
    );
  }

  syncTrainingFiltersFieldWithTour(
    showDropdown: boolean,
    action: 'focus' | 'blur' | 'pointerdown' | 'dropdown',
  ): void {
    if (this.blockTourFieldSyncWhileTipOpen(() => this.isTrainingFiltersStepActive())) {
      return;
    }
    this.syncAutocompleteTourField(
      () => this.isTrainingFiltersStepActive(),
      setTrainingFiltersInteractionActive,
      resetTrainingFiltersTargetPadding,
      resolveTrainingFiltersTarget,
      showDropdown,
      action,
      false,
    );
  }

  syncMessagesConnectionsSearchFieldWithTour(
    showDropdown: boolean,
    action: 'focus' | 'blur' | 'pointerdown' | 'dropdown',
  ): void {
    if (this.blockTourFieldSyncWhileTipOpen(() => this.isMessagesConnectionsSearchStepActive())) {
      return;
    }
    this.syncAutocompleteTourField(
      () => this.isMessagesConnectionsSearchStepActive(),
      setMessagesConnectionsSearchInteractionActive,
      resetMessagesConnectionsSearchTargetPadding,
      resolveMessagesConnectionsSearchTarget,
      showDropdown,
      action,
      false,
    );
  }

  registerMessagesConnectionsTour(): void {
    this.registerTour(MESSAGES_CONNECTIONS_TOUR, (tour) => buildMessagesConnectionsTour(tour));
  }

  registerMessagesGroupChatTour(): void {
    this.registerTour(MESSAGES_GROUP_CHAT_TOUR, (tour) => buildMessagesGroupChatTour(tour));
  }

  registerTeeTimePollTour(): void {
    this.registerTour(TEE_TIME_POLL_TOUR, (tour) => buildTeeTimePollTour(tour));
  }

  isTeeTimePollTourActive(): boolean {
    const t = this.activeTour;
    if (!t || !this.isShepherdActiveTour(t)) {
      return false;
    }
    return (t.options?.id as string | undefined) === TEE_TIME_POLL_TOUR;
  }

  isTeeTimePollCoursesStepActive(): boolean {
    if (!this.isTeeTimePollTourActive()) {
      return false;
    }
    const t = this.activeTour;
    if (!t) {
      return false;
    }
    return isShepherdTourStepInteractive(t, TEE_TIME_POLL_STEP_COURSES);
  }

  /** Tee time poll modal tour tip is open (courses / dates / times steps). */
  isTeeTimePollTourTipOpen(): boolean {
    if (!this.isTeeTimePollTourActive()) {
      return false;
    }
    if (typeof document === 'undefined') {
      return false;
    }
    return document.body.classList.contains(SHEPHERD_TOUR_CLICK_SHIELD_CLASS);
  }

  private notifyTourUiChanged(): void {
    this.ngZone.run(() => this.tourUiChanged.next());
  }

  private blockTourFieldSyncWhileTipOpen(isTipOpen: () => boolean): boolean {
    return isTipOpen();
  }

  syncTeeTimePollCoursesFieldWithTour(
    showDropdown: boolean,
    action: 'focus' | 'blur' | 'pointerdown' | 'dropdown',
  ): void {
    if (this.blockTourFieldSyncWhileTipOpen(() => this.isTeeTimePollCoursesStepActive())) {
      return;
    }
    this.syncAutocompleteTourField(
      () => this.isTeeTimePollCoursesStepActive(),
      setTeeTimePollCoursesInteractionActive,
      resetTeeTimePollCoursesTargetPadding,
      resolveTeeTimePollCoursesTarget,
      showDropdown,
      action,
      false,
    );
  }

  /** Finish explore tour after a successful join while on the join-request step (any activity card). */
  completeExploreActivityTourIfOnJoinStep(): void {
    const t = this.activeTour;
    if (!t || !this.isShepherdActiveTour(t) || !this.isExploreActivityTourActive()) {
      return;
    }
    const stepId = (t.getCurrentStep?.() as { id?: string } | undefined)?.id;
    if (stepId !== EXPLORE_JOIN_REQUEST_STEP_ID) {
      return;
    }
    void t.complete();
  }

  private isExploreActivityTourActive(): boolean {
    const tourId = this.activeTour?.options?.id as string | undefined;
    return tourId === EXPLORE_ACTIVITY_TOUR || tourId === EXPLORE_ACTIVITY_CONTINUATION_TOUR;
  }

  /** Finish poll tour when user leaves the times wizard step via Next (same as Finish on the tip). */
  completeTeeTimePollTourIfOnTimesStep(): void {
    const t = this.activeTour;
    if (!t || !this.isTeeTimePollTourActive() || !this.isShepherdActiveTour(t)) {
      return;
    }
    const stepId = (t.getCurrentStep?.() as { id?: string } | undefined)?.id;
    if (stepId !== TEE_TIME_POLL_STEP_TIMES) {
      return;
    }
    void t.complete();
  }

  /** Hide the current poll tour tip before the modal wizard page changes. */
  prepareTeeTimePollTourBeforeModalStepChange(modalStep: number): void {
    const t = this.activeTour;
    if (!t || !this.isTeeTimePollTourActive()) {
      return;
    }
    prepareTeeTimePollTourForModalStep(t, modalStep);
  }

  /** Show the tour tip that matches the current poll wizard step (2–4). */
  showTeeTimePollTourStepForModalStep(modalStep: number): void {
    const t = this.activeTour;
    if (!t || !this.isTeeTimePollTourActive()) {
      return;
    }
    const stepId = getTeeTimePollTourStepIdForModalStep(modalStep);
    if (!stepId) {
      return;
    }
    const targetStep = t.getById?.(stepId) as ShepherdStep | undefined;
    if (
      shouldSkipShepherdStepReshow(t, stepId) ||
      wasShepherdStepDismissedByUser(targetStep)
    ) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      showTeeTimePollTourStepWhenReady(t, modalStep);
    });
  }

  registerTrainingBookTrainerTour(): void {
    this.registerTour(TRAINING_BOOK_TRAINER_TOUR, (tour) => buildTrainingBookTrainerTour(tour));
  }

  registerTrainingBookSessionTour(): void {
    this.registerTour(TRAINING_BOOK_SESSION_TOUR, (tour) => buildTrainingBookSessionTour(tour));
  }

  /** True when the golfer “Book a trainer” list tour is the active Shepherd tour. */
  isTrainingBookListTourActive(): boolean {
    const t = this.activeTour;
    if (!t || !this.isShepherdActiveTour(t)) {
      return false;
    }
    return (t.options?.id as string | undefined) === TRAINING_BOOK_TRAINER_TOUR;
  }

  /** List tour step tooltip is open (user has not tapped OK / Skip / Finish / ✕). */
  isTrainingBookListTourTipOpen(): boolean {
    const t = this.activeTour;
    if (!t || !this.isTrainingBookListTourActive()) {
      return false;
    }
    const stepId = this.getCurrentStepSafe(t)?.id;
    return !!stepId && isShepherdTourStepInteractive(t, stepId);
  }

  /** Session booking tour tooltip is open on `/training/book/:id`. */
  isTrainingBookSessionTourTipOpen(): boolean {
    const t = this.activeTour;
    if (!t || !this.isShepherdActiveTour(t)) {
      return false;
    }
    if ((t.options?.id as string | undefined) !== TRAINING_BOOK_SESSION_TOUR) {
      return false;
    }
    const stepId = this.getCurrentStepSafe(t)?.id;
    return !!stepId && isShepherdTourStepInteractive(t, stepId);
  }

  /**
   * `/training` list page: auto-start list tour (same gate as other shepherd demos: env flag + token).
   */
  tryStartTrainingBookTrainerTour(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    const path = this.router.url.split('?')[0].split('#')[0];
    if (path !== '/training' && path !== '/training/') {
      return;
    }
    this.registerTrainingBookTrainerTour();
    this.startTour(TRAINING_BOOK_TRAINER_TOUR, true);
  }

  /**
   * After search on `/training`, show the results-area tip (trainer cards or empty state).
   */
  advanceTrainingBookTrainerTourAfterSearch(): void {
    this.tryAdvanceTrainingBookTrainerToResultsStep();
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => this.tryAdvanceTrainingBookTrainerToResultsStep(), 400);
      setTimeout(() => this.tryAdvanceTrainingBookTrainerToResultsStep(), 900);
      setTimeout(() => this.tryAdvanceTrainingBookTrainerToResultsStep(), 2000);
    });
  }

  private tryAdvanceTrainingBookTrainerToResultsStep(): void {
    const tour = this.activeTour;
    if (!tour || !this.isTrainingBookListTourActive() || !this.isShepherdActiveTour(tour)) {
      return;
    }
    const step = this.getCurrentStepSafe(tour);
    if (!step || step.id !== TRAINING_FILTERS_STEP_ID) {
      return;
    }
    const resultsStep = tour.getById?.(TRAINING_RESULTS_STEP_ID) as ShepherdStep | undefined;
    if (
      shouldSkipShepherdStepReshow(tour, TRAINING_RESULTS_STEP_ID) ||
      wasShepherdStepDismissedByUser(resultsStep)
    ) {
      return;
    }
    if (!document.querySelector('[data-tour="training-results-panel"]')) {
      return;
    }
    this.ngZone.run(() => tryShowTrainingResultsStepIfReady(tour));
  }

  /**
   * `/training/book/:id`: run session steps when user came from the list tour (arm via OK on step 2 or Book while tour active).
   */
  tryStartTrainingBookSessionTourIfPending(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    let pending = false;
    try {
      pending = sessionStorage.getItem(TRAINING_BOOK_SESSION_TOUR_PENDING_KEY) === '1';
    } catch {
      return;
    }
    if (!pending) {
      return;
    }
    const path = this.router.url.split('?')[0].split('#')[0];
    if (!/^\/training\/book\//.test(path)) {
      return;
    }
    this.registerTrainingBookSessionTour();
    this.startTour(TRAINING_BOOK_SESSION_TOUR, true);
  }

  registerSchedulePageTour(): void {
    this.registerTour(SCHEDULE_PAGE_TOUR, (tour) => buildSchedulePageTour(tour));
  }

  /**
   * `/schedule`: tab intro (same gate as other shepherd demos: env flag + token).
   */
  tryStartSchedulePageTour(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    const path = this.router.url.split('?')[0].split('#')[0];
    if (!this.isSchedulePagePath(path)) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const p = this.router.url.split('?')[0].split('#')[0];
        if (!this.isSchedulePagePath(p)) {
          return;
        }
        this.registerSchedulePageTour();
        this.startTour(SCHEDULE_PAGE_TOUR, true);
      }, 600);
    });
  }

  /**
   * Auto-start: Group Chat tab tour after the user opens that tab (testing flag + logged in).
   */
  tryStartMessagesGroupChatTour(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    const path = this.router.url.split('?')[0].split('#')[0];
    if (!path.includes('messages')) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const deadline = Date.now() + 16000;
      const tick = (): void => {
        const p = this.router.url.split('?')[0].split('#')[0];
        if (!p.includes('messages')) {
          return;
        }
        if (!isGroupChatsTabActiveInDom()) {
          if (Date.now() < deadline) {
            setTimeout(tick, 200);
          }
          return;
        }
        if (!document.querySelector('[data-tour="messages-group-chats-create-icon"]')) {
          if (Date.now() < deadline) {
            setTimeout(tick, 200);
          }
          return;
        }
        if (isMessagesGroupChatsSkeletonVisible()) {
          if (Date.now() < deadline) {
            setTimeout(tick, 200);
          }
          return;
        }
        this.registerMessagesGroupChatTour();
        this.startTour(MESSAGES_GROUP_CHAT_TOUR, true);
      };
      setTimeout(tick, 400);
    });
  }

  /**
   * Auto-start: Connections tab intro on `/messages` (testing flag + logged in).
   * Waits for the connections list to finish loading; runs when empty or when connections exist.
   */
  tryStartMessagesConnectionsTour(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    const path = this.router.url.split('?')[0].split('#')[0];
    if (!path.includes('messages')) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const deadline = Date.now() + 16000;
      const tick = (): void => {
        const p = this.router.url.split('?')[0].split('#')[0];
        if (!p.includes('messages')) {
          return;
        }
        if (!document.querySelector('[data-tour="messages-connections-search"]')) {
          if (Date.now() < deadline) {
            setTimeout(tick, 200);
          }
          return;
        }
        if (isMessagesConnectionsSkeletonVisible()) {
          if (Date.now() < deadline) {
            setTimeout(tick, 200);
          }
          return;
        }
        if (!messagesConnectionsHasEmptyState() && !messagesConnectionsHasList()) {
          if (Date.now() < deadline) {
            setTimeout(tick, 200);
          }
          return;
        }
        this.registerMessagesConnectionsTour();
        this.startTour(MESSAGES_CONNECTIONS_TOUR, true);
      };
      setTimeout(tick, 500);
    });
  }

  /**
   * Auto-start post-activity tutorial on full-page `/add-activity` when the create modal is closed
   * (avoids clashing with the modal-driven tour).
   */
  tryStartPostActivityTourForAddActivityRoute(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    const path = this.router.url.split('?')[0];
    if (!path.includes('add-activity')) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const p = this.router.url.split('?')[0];
        if (!p.includes('add-activity')) {
          return;
        }
        const toggle = document.getElementById('create-golf-event-toggle') as HTMLInputElement | null;
        if (toggle?.checked) {
          return;
        }
        this.registerPostActivityTour();
        this.startTour(POST_ACTIVITY_TOUR, true);
      }, 900);
    });
  }

  /** Start post-activity tutorial after the create-activity modal is open (checkbox checked). */
  /** Start tee time poll tutorial when the modal is on the golf-courses step (step 2). */
  tryStartTeeTimePollTour(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const toggle = document.getElementById('toggleTeeTimePollModal') as HTMLInputElement | null;
        if (!toggle?.checked) {
          return;
        }
        if (!document.querySelector('[data-tour="tee-time-poll-golf-courses"]')) {
          return;
        }
        this.registerTeeTimePollTour();
        this.startTour(TEE_TIME_POLL_TOUR, true);
      }, 400);
    });
  }

  tryStartPostActivityTourOnModalOpen(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const toggle = document.getElementById('create-golf-event-toggle') as HTMLInputElement | null;
        if (!toggle?.checked) {
          return;
        }
        if (this.isPostActivityTourActive()) {
          return;
        }
        this.registerPostActivityTour();
        this.startTour(POST_ACTIVITY_TOUR, true);
      }, 400);
    });
  }

  /**
   * Auto-start: home dashboard tutorial on `/` or `/home` (when shepherd flag + logged in).
   */
  tryStartHomePageTourForTesting(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    if (!this.isHomePagePath(this.router.url)) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (!this.isHomePagePath(this.router.url)) {
          return;
        }
        this.registerHomePageTour();
        this.startTour(HOME_PAGE_TOUR, true);
      }, 800);
    });
  }

  /**
   * Auto-start: explore tutorial on `/explore-activity` only (when testing flag + logged in).
   */
  tryStartExploreActivityTourForTesting(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    if (!this.isExploreActivityPath(this.router.url)) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (!this.isExploreActivityPath(this.router.url)) {
          return;
        }
        this.registerExploreActivityTour();
        this.startTour(EXPLORE_ACTIVITY_TOUR, true);
      }, 800);
    });
  }

  /**
   * After the user runs a search from the explore search-wait step, advance the tour.
   */
  advanceExploreTourAfterSearch(): void {
    this.tryAdvanceExploreFromSearchWait();
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => this.tryAdvanceExploreFromSearchWait(), 400);
      setTimeout(() => this.tryAdvanceExploreFromSearchWait(), 900);
      setTimeout(() => this.tryAdvanceExploreFromSearchWait(), 2000);
      setTimeout(() => this.tryAdvanceExploreFromSearchWait(), 3500);
    });
  }

  advanceExploreTourIfStillOnSearchWait(): void {
    this.tryAdvanceExploreFromSearchWait();
  }

  private tryAdvanceExploreFromSearchWait(): void {
    const tour = this.activeTour;
    if (!tour) {
      return;
    }
    const step = this.getCurrentStepSafe(tour);
    if (!step || step.id !== EXPLORE_SEARCH_WAIT_STEP_ID) {
      return;
    }
    this.ngZone.run(() => {
      const t = this.activeTour;
      if (!t) {
        return;
      }
      const cur = this.getCurrentStepSafe(t);
      if (!cur || cur.id !== EXPLORE_SEARCH_WAIT_STEP_ID) {
        return;
      }
      void t.next();
    });
  }

  tryExploreContinuationTourAfterSearchFallback(): void {
    if (!environment.shepherdExploreTour) {
      return;
    }
    if (!localStorage.getItem('token')) {
      return;
    }
    if (!this.isExploreActivityPath(this.router.url)) {
      return;
    }
    if (!document.querySelector('[data-tour="explore-results-panel"]')) {
      return;
    }

    const t = this.activeTour;
    if (t && this.isShepherdActiveTour(t)) {
      const sid = this.getCurrentStepSafe(t)?.id;
      if (sid === EXPLORE_SEARCH_WAIT_STEP_ID) {
        this.ngZone.run(() => void t.next());
      }
      return;
    }

    this.registerExploreActivityTour();
    this.startTour(EXPLORE_ACTIVITY_CONTINUATION_TOUR, true);
  }

  ngOnDestroy(): void {
    if (this.activeTour && this.isShepherdActiveTour(this.activeTour)) {
      this.suppressPermanentSkipOnTourCancel = true;
      void this.activeTour.cancel();
    }
    this.activeTour = null;
    this.routerSub?.unsubscribe();
  }
}
